import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { Route, Outlet, Routes, Navigate, useLocation } from "react-router-dom";
import { Howl } from "howler";
import { RootState } from "app/store";
import { useScale } from "common/utils";
import { UserBtn } from "features/user";
import { HomePage } from "features/home";
import { AuthPageRoutes } from "features/auth";
import { TopTutorial } from "features/tutorial";
import { UniversalBtn } from "features/universal";
import { CoursePageRoutes } from "features/course";
import { ParentSettingPage } from "features/parent";
import { DialogPage } from "features/top/DialogPage";
import { CreatorTop, CreatorMain } from "features/creator";
import { NotificationDetail } from "features/notification";
import { StartupScreen } from "./StartupScreen";
import { useInitialApp } from "./useInitialApp";
import { useInitialAppConfig } from "./useInitialAppConfig";

export type ConfigContext = {
  bgmSound: Howl;
  audioContext: AudioContext;
};

const AppLayout = ({ bgmSound, audioContext }: ConfigContext) => (
  <div className="flex-col-el relative w-full h-full">
    <Outlet context={{ bgmSound, audioContext }} />
  </div>
);

const AppRoutes = () => {
  const location = useLocation();
  const { bgmSound, audioContext } = useInitialAppConfig();
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);

  useEffect(() => {
    return () => {
      if (/\/courses\/[a-zA-Z0-9_]+/.test(location.pathname)) {
        const canvas = document.getElementById(
          "app-canvas",
        ) as unknown as HTMLCanvasElement;
        if (canvas) {
          canvas.style.opacity = "0";
        }
      }
    };
  }, [location]);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={location.key || location.pathname + location.search}
        classNames="fade"
        timeout={400}
      >
        <Routes location={location}>
          <Route path="/">
            <Route index element={<StartupScreen />} />

            {parent && active && (
              <Route
                path="/"
                element={
                  <AppLayout bgmSound={bgmSound} audioContext={audioContext} />
                }
              >
                <Route path="/top" element={<HomePage />} />
                <Route path="/courses">{CoursePageRoutes}</Route>
                <Route path="/creator">
                  <Route index element={<CreatorTop />} />
                  <Route path=":id" element={<CreatorMain />} />
                </Route>
              </Route>
            )}

            <Route path="auth/*" element={<AuthPageRoutes />} />
            <Route path="/*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </CSSTransition>
    </SwitchTransition>
  );
};

export const AppIndexPage = () => {
  useInitialApp();
  const { scale } = useScale();
  const active = useSelector((state: RootState) => state.user.active);

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        transition={Slide}
        hideProgressBar={true}
        style={{
          zIndex: 9999999,
          left: "50%",
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: "top",
        }}
      />
      <DialogPage />
      <ParentSettingPage />
      <NotificationDetail />

      {active &&
        location.pathname !== "/" &&
        !location.pathname.includes("/auth") && (
          <>
            <UserBtn />
            <TopTutorial />
            <UniversalBtn />
          </>
        )}

      <AppRoutes />
    </>
  );
};
